import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import NFTCard from "@/components/NFTCard";
import MintModal from "@/components/MintModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Link2, Copy, BookOpen, Layers, Sparkles, Hash } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [mintOpen, setMintOpen] = useState(false);

  // class/assignment forms
  const [showClass, setShowClass] = useState(false);
  const [className, setClassName] = useState("");
  const [classDesc, setClassDesc] = useState("");

  const [showAsn, setShowAsn] = useState(false);
  const [asnTitle, setAsnTitle] = useState("");
  const [asnDesc, setAsnDesc] = useState("");
  const [asnClass, setAsnClass] = useState("");

  // join class (student)
  const [joinCode, setJoinCode] = useState("");

  const loadAll = async () => {
    const [f, cat, cls, asn] = await Promise.all([
      api.get("/assets/mine", { params: { tab: "files" } }),
      api.get("/assets/mine", { params: { tab: "catalog" } }),
      api.get("/classes"),
      api.get("/assignments"),
    ]);
    setFiles(f.data);
    setCatalog(cat.data);
    setClasses(cls.data);
    setAssignments(asn.data);
    if (user?.role === "teacher") {
      const a = await api.get("/teacher/approvals");
      setApprovals(a.data);
    }
  };

  useEffect(() => {
    if (user) loadAll();
    // eslint-disable-next-line
  }, [user]);

  if (!user) return null;

  const createClass = async () => {
    try {
      await api.post("/classes", { name: className, description: classDesc });
      setClassName(""); setClassDesc(""); setShowClass(false);
      toast.success("Class created");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createAsn = async () => {
    try {
      await api.post("/assignments", { class_id: asnClass, title: asnTitle, description: asnDesc });
      setAsnTitle(""); setAsnDesc(""); setShowAsn(false);
      toast.success("Assignment created");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const joinClass = async () => {
    try {
      const { data } = await api.post(`/classes/join/${joinCode.trim()}`);
      toast.success("Joined class!");
      setJoinCode("");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyJoinLink = (code) => {
    const url = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Join link copied");
  };

  const togglePublicProfile = async () => {
    try {
      await api.patch("/auth/profile", { public_profile: !user.public_profile });
      refresh();
      toast.success("Profile visibility updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Welcome back</div>
          <h1 className="font-display text-4xl tracking-tight mt-2">{user.name}</h1>
          <div className="text-sm text-zinc-500 mt-1">{user.school_name} · <span className="capitalize">{user.role}</span></div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-zinc-400 border border-white/10 rounded-md px-3 py-2">
            Public Profile
            <Switch data-testid="toggle-public-profile" checked={user.public_profile} onCheckedChange={togglePublicProfile} />
          </div>
          <Button data-testid="mint-open-btn" onClick={() => setMintOpen(true)} className="bg-white text-black hover:bg-zinc-200">
            <Plus size={16} className="mr-1" /> Mint
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
        <Stat icon={<Layers size={16} />} label="Files" value={files.length} />
        <Stat icon={<Sparkles size={16} />} label="Catalog" value={catalog.length} />
        <Stat icon={<BookOpen size={16} />} label="Classes" value={classes.length} />
        <Stat icon={<Hash size={16} />} label="Assignments" value={assignments.length} />
      </div>

      <div className="mt-10">
        <Tabs defaultValue="files">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start gap-6 overflow-x-auto">
            <TabsTrigger data-testid="dash-tab-files" value="files" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">Files</TabsTrigger>
            <TabsTrigger data-testid="dash-tab-catalog" value="catalog" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">Catalog</TabsTrigger>
            <TabsTrigger data-testid="dash-tab-classes" value="classes" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">Classes</TabsTrigger>
            <TabsTrigger data-testid="dash-tab-assignments" value="assignments" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">Assignments</TabsTrigger>
            {user.role === "teacher" && (
              <TabsTrigger data-testid="dash-tab-approvals" value="approvals" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">
                Approvals {approvals.length > 0 && <span className="ml-1 text-cyan8r">({approvals.length})</span>}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="files" className="mt-8">
            {files.length === 0 ? (
              <Empty msg="No files yet. Mint your first asset." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {files.map((a) => <NFTCard key={a.id} asset={a} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="catalog" className="mt-8">
            {catalog.length === 0 ? (
              <Empty msg="No public assets yet." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {catalog.map((a) => <NFTCard key={a.id} asset={a} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="classes" className="mt-8 space-y-6">
            {user.role === "teacher" ? (
              <div className="flex items-center gap-3">
                <Dialog open={showClass} onOpenChange={setShowClass}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-class-btn" className="bg-white text-black hover:bg-zinc-200"><Plus size={14} className="mr-1"/>Create Class</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0F0F0F] border-white/10 text-white">
                    <DialogHeader><DialogTitle>New Class</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Class name</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" /></div>
                      <div><Label>Description</Label><Textarea value={classDesc} onChange={(e) => setClassDesc(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" /></div>
                      <Button data-testid="submit-class-btn" onClick={createClass} className="bg-white text-black hover:bg-zinc-200 w-full">Create</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            ) : (
              <div className="flex gap-2 items-end">
                <div className="flex-1 max-w-sm">
                  <Label>Join code</Label>
                  <Input data-testid="join-code-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Paste join code" className="bg-[#0A0A0A] border-white/10 mt-1" />
                </div>
                <Button data-testid="join-class-btn" onClick={joinClass} className="bg-white text-black hover:bg-zinc-200">Join</Button>
              </div>
            )}
            {classes.length === 0 ? (
              <Empty msg={user.role === "teacher" ? "Create your first class." : "Join a class with a code."} />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {classes.map((c) => (
                  <div key={c.id} className="card-surface p-5">
                    <div className="font-display text-lg">{c.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{c.student_ids?.length || 0} students · {c.teacher_name}</div>
                    {c.description && <div className="text-sm text-zinc-400 mt-2">{c.description}</div>}
                    {user.role === "teacher" && (
                      <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
                        <span className="font-mono text-xs text-zinc-500">{c.join_code}</span>
                        <button data-testid={`copy-join-${c.id}`} onClick={() => copyJoinLink(c.join_code)} className="flex items-center gap-1 text-xs text-cyan8r hover:underline">
                          <Copy size={12} /> Copy join link
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="assignments" className="mt-8 space-y-6">
            {user.role === "teacher" && classes.length > 0 && (
              <Dialog open={showAsn} onOpenChange={setShowAsn}>
                <DialogTrigger asChild>
                  <Button data-testid="create-assignment-btn" className="bg-white text-black hover:bg-zinc-200"><Plus size={14} className="mr-1"/>Create Assignment</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0F0F0F] border-white/10 text-white">
                  <DialogHeader><DialogTitle>New Assignment</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Class</Label>
                      <select value={asnClass} onChange={(e) => setAsnClass(e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 mt-1 text-white">
                        <option value="">Select class</option>
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div><Label>Title</Label><Input value={asnTitle} onChange={(e) => setAsnTitle(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" /></div>
                    <div><Label>Description</Label><Textarea value={asnDesc} onChange={(e) => setAsnDesc(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" /></div>
                    <Button data-testid="submit-assignment-btn" onClick={createAsn} className="bg-white text-black hover:bg-zinc-200 w-full">Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {assignments.length === 0 ? (
              <Empty msg="No assignments yet." />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {assignments.map((a) => (
                  <div key={a.id} className="card-surface p-5">
                    <div className="text-xs uppercase tracking-wider text-cyan8r">{a.class_name}</div>
                    <div className="font-display text-lg mt-1">{a.title}</div>
                    {a.description && <div className="text-sm text-zinc-400 mt-2">{a.description}</div>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {user.role === "teacher" && (
            <TabsContent value="approvals" className="mt-8">
              {approvals.length === 0 ? (
                <Empty msg="All caught up. No pending mints." />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {approvals.map((a) => <NFTCard key={a.id} asset={a} />)}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      <MintModal open={mintOpen} onOpenChange={setMintOpen} onMinted={loadAll} />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="card-surface p-5">
      <div className="text-cyan8r">{icon}</div>
      <div className="text-3xl font-display mt-2">{value}</div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="border border-dashed border-white/10 rounded-xl py-16 text-center text-zinc-500">{msg}</div>
  );
}
